import {
  Button,
  FormControlLabel,
  Radio,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { NotificationPolicyTypeEnum } from '@repo/api-gw-sdk';
import Image from 'next/image';
import { useCallback } from 'react';

import type { StepProps } from '@/components/wizard/StepProps';
import { useEnvironment } from '@/contexts/useEnvironment';
import { useFeatureFlags } from '@/contexts/useFeatureFlags';

import {
  NotificationPolicyTypes,
  splunkSupportedNotificationTypes,
} from './data';
import type { UpsertNotificationPolicyState } from './upsertNotificationPolicyFlow';

export const NotificationPolicyNameSelection = (
  props: StepProps<UpsertNotificationPolicyState>
) => {
  const { isDemo, isDev } = useEnvironment();
  const { isServiceNowNotificationEnabled } = useFeatureFlags();

  const next = () => {
    if (props.wizardState.type === NotificationPolicyTypeEnum.Splunk) {
      props.setWizardState((state) => ({
        ...state,
        notificationTypes: splunkSupportedNotificationTypes,
      }));
    }
    props.setNextStep(props.currentStep.next?.[0]);
  };

  const validateParams = useCallback(
    (name: string): boolean => {
      return name?.length >= 4 && !!props.wizardState.type;
    },
    [props.wizardState.type]
  );

  return (
    <Stack
      sx={{ height: '100%' }}
      alignItems='center'
      justifyContent='center'
      gap='24px'
    >
      <Typography variant='h3' sx={{ textAlign: 'center' }}>
        Notification policy name
      </Typography>
      <TextField
        autoFocus
        sx={{
          width: '655px',
          '& .MuiOutlinedInput-root': {
            padding: '8px',
          },
        }}
        placeholder='Example: Slack #eon-notifications'
        onChange={(e) =>
          props.setWizardState((state) => ({
            ...state,
            name: e.target.value,
          }))
        }
        onKeyDown={(e) => {
          if (e.key === 'Enter' && validateParams(props.wizardState.name)) {
            next();
          }
        }}
        value={props.wizardState.name}
        slotProps={{
          input: {
            endAdornment: (
              <Button
                sx={{ width: '200px' }}
                variant='contained'
                disabled={!validateParams(props.wizardState.name)}
                onClick={next}
              >
                Next
                <i className='material-symbols-arrow-right-alt ml-[8px]' />
              </Button>
            ),
          },
        }}
      />
      <Stack direction='row'>
        {Object.values(NotificationPolicyTypeEnum)
          .filter(
            (x) =>
              isDemo ||
              isDev ||
              x === NotificationPolicyTypeEnum.Splunk ||
              x === NotificationPolicyTypeEnum.Slack ||
              x === NotificationPolicyTypeEnum.Sns ||
              x === NotificationPolicyTypeEnum.Email ||
              x === NotificationPolicyTypeEnum.Webhook ||
              (x === NotificationPolicyTypeEnum.ServiceNow &&
                isServiceNowNotificationEnabled)
          )
          .map((x) => (
            <div key={x}>
              <FormControlLabel
                control={
                  <Radio
                    disableRipple
                    sx={{ padding: '10px 10px 10px 30px' }}
                    size='small'
                    checked={x === props.wizardState.type}
                    onChange={() =>
                      props.setWizardState((state) => ({
                        ...state,
                        type: x,
                      }))
                    }
                  />
                }
                label={
                  <Stack direction='row' alignItems='center' gap='8px'>
                    <Image
                      alt={NotificationPolicyTypes[x]?.title}
                      src={NotificationPolicyTypes[x]?.logo}
                      width={24}
                      height={24}
                      style={{ width: '24px', height: '24px' }}
                    />
                    <Typography>{NotificationPolicyTypes[x]?.title}</Typography>
                  </Stack>
                }
              />
            </div>
          ))}
      </Stack>
    </Stack>
  );
};
